import { FormatDateOptions, IntlShape } from 'react-intl';

import { DEFAULT_LANG, extractLanguage } from '@/configuration/lang/lang';

import { EN_ORDINAL_SUFFIX } from './constants';

const DAY_DATE_OPTIONS: FormatDateOptions = { year: 'numeric', month: 'long', day: 'numeric' };

/**
 * Formats a day-bearing date for the example question templates. The backend renders the same templates and matches
 * submitted questions by exact text, so English uses its ordinal form ("October 1st, 2026") while other languages keep
 * their native long date ("1. Oktober 2026", "1 octobre 2026").
 *
 * @param intl - react-intl instance of the active locale
 * @param date - Date to format
 * @returns The formatted date
 */
const formatDayDate = (intl: IntlShape, date: Date): string => {
  if (extractLanguage(intl.locale) !== DEFAULT_LANG) {
    return intl.formatDate(date, DAY_DATE_OPTIONS);
  }

  const day = date.getDate();
  const suffix = EN_ORDINAL_SUFFIX[intl.formatPlural(day, { type: 'ordinal' })];

  return `${intl.formatDate(date, { month: 'long' })} ${day}${suffix}, ${date.getFullYear()}`;
};

/**
 * Builds the date values that fill the placeholders of the example question templates.
 *
 * @param intl - react-intl instance of the active locale
 * @returns Formatted date strings keyed by placeholder name
 */
const getDatesToTranslations = (intl: IntlShape) => {
  const now = new Date();
  const lastMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
  const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const lastMonthDate = new Date(year, lastMonth);
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const currentMonthDate = new Date(now.getFullYear(), now.getMonth());
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);

  const currentQuarter = Math.ceil(currentMonth / 3);
  let lastQuarter = currentQuarter - 1;
  let yearOfLastQuarter = currentYear;

  if (lastQuarter === 0) {
    lastQuarter = 4;
    yearOfLastQuarter--;
  }

  const quarterStartMonth = (lastQuarter - 1) * 3 + 1;
  const quarterEndMonth = quarterStartMonth + 2;

  const startDate = new Date(year, lastMonth, 1);
  const endDate = new Date(year, lastMonth + 1, 0);
  const threeMonthsAgoMonth = now.getMonth() - 3 < 0 ? 12 + (now.getMonth() - 3) : now.getMonth() - 3;
  const threeMonthsAgoYear = now.getMonth() - 3 < 0 ? now.getFullYear() - 1 : now.getFullYear();
  const threeMonthsAgoDate = new Date(threeMonthsAgoYear, threeMonthsAgoMonth);
  const sixMonthsAgoMonth = now.getMonth() - 6 < 0 ? 12 + (now.getMonth() - 6) : now.getMonth() - 6;
  const sixMonthsAgoYear = now.getMonth() - 6 < 0 ? now.getFullYear() - 1 : now.getFullYear();
  const sixMonthsAgoDate = new Date(sixMonthsAgoYear, sixMonthsAgoMonth);
  const startQuarterDate = new Date(yearOfLastQuarter, quarterStartMonth - 1, 1);
  const endQuarterDate = new Date(yearOfLastQuarter, quarterEndMonth, 0);
  const twoWeeksAgoDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 14);
  const oneWeekAgoDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);

  return {
    currentMonth: intl.formatDate(currentMonthDate, { year: 'numeric', month: 'long' }),
    threeMonthsAgo: formatDayDate(intl, threeMonthsAgoDate),
    threeMonthsAgoMonth: intl.formatDate(threeMonthsAgoDate, { year: 'numeric', month: 'long' }),
    sixMonthsAgo: formatDayDate(intl, sixMonthsAgoDate),
    lastMonth: intl.formatDate(lastMonthDate, { year: 'numeric', month: 'long' }),
    startLastMonth: formatDayDate(intl, startDate),
    endLastMonth: formatDayDate(intl, endDate),
    startQuarter: formatDayDate(intl, startQuarterDate),
    endQuarter: formatDayDate(intl, endQuarterDate),
    startQuarterMonth: intl.formatDate(startQuarterDate, { year: 'numeric', month: 'long' }),
    endQuarterMonth: intl.formatDate(endQuarterDate, { year: 'numeric', month: 'long' }),
    twoWeeksAgo: formatDayDate(intl, twoWeeksAgoDate),
    oneWeekAgo: formatDayDate(intl, oneWeekAgoDate),
    yesterday: formatDayDate(intl, yesterday),
  };
};
export default getDatesToTranslations;
